import React, { useState, useEffect, useRef } from "react";
import { PageLayout } from "../../components/page-layout";
import { OnboardingProgressBar } from "../../components/onboarding-progress-bar";
import { useSearchParams } from "react-router-dom";
import { useOnboarding } from "../../providers/onboarding-provider";
import HelloSign from 'hellosign-embedded';
import { getEmbeddedSigningUrls, getConsentStatus, downloadConsentDocument } from "../../services/backend.service";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLoader } from "../../components/page-loader";
import { CheckCircle } from '@mui/icons-material';
import { FormShell } from "../../components/onyx/FormShell";
import { Button } from "../../components/onyx/Button";
import { Panel } from "../../components/onyx/Panel";
import { Text } from "../../components/onyx/Text";
import { FormActions } from "../../components/onyx/Field";
import { OnyxBanner } from "../../components/onyx/OnyxBanner";
import { CircularProgress, Chip } from '@mui/material';

const DOCUMENT_NAMES = {
    'cosigned': 'Onyx Informed Consent Form',
    'pluto': 'Pluto Health Consent Form'
};

const POST_SIGN_POLL_ATTEMPTS = 8;
const POST_SIGN_POLL_DELAY_MS = 1500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const memberSignedDocTypes = (statusData) => {
    const completed = [];
    const consents = statusData?.consents || {};
    Object.entries(consents).forEach(([key, value]) => {
        if (value && typeof value === 'object' && value.member_signed) {
            completed.push(key);
        }
    });
    return completed;
};

const ConsentFormComponent = ({
    documentType,
    title,
    description,
    onSign,
    isSigned,
    isLoading,
    onDownload,
}) => {
    return (
        <Panel
          tone={isSigned ? 'success' : 'default'}
          title={(
            <>
              {title}
              {isSigned ? (
                <CheckCircle
                  className="ml-1"
                  style={{ color: 'var(--onyx-green-gold)', fontSize: '2rem', verticalAlign: 'middle' }}
                />
              ) : null}
            </>
          )}
        >
            <Text variant="body-m">{description}</Text>
            <FormActions>
                {!isSigned ? (
                    <Button
                        type="button"
                        variant="primary"
                        onClick={() => onSign(documentType)}
                        disabled={isLoading}
                        startIcon={
                            isLoading ? (
                                <CircularProgress
                                    size={20}
                                    style={{ color: 'var(--onyx-purple-dark)' }}
                                />
                            ) : undefined
                        }
                    >
                        {isLoading ? 'Verifying...' : 'Review and Sign Form'}
                    </Button>
                ) : (
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onDownload(documentType)}
                    >
                        Download Signed Form
                    </Button>
                )}
            </FormActions>
        </Panel>
    );
};

export const ConsentPage = () => {
    const [searchParams] = useSearchParams();
    const { getAccessTokenSilently } = useAuth0();
    const DROPBOX_SIGN_CLIENT_ID = process.env.REACT_APP_DROPBOX_SIGN_CLIENT_ID;
    const { completeStep } = useOnboarding();
    
    // Check if user manually navigated here (e.g., clicked on progress bar)
    const isManualNavigation = searchParams.get('source') === 'manual';
    const [isSigningVisible, setIsSigningVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updatingAfterSign, setUpdatingAfterSign] = useState(false); // New state for post-sign loading
    const [error, setError] = useState(null);
    const [consentStatus, setConsentStatus] = useState(null);
    const [signingUrls, setSigningUrls] = useState(null);
    
    const [allDocuments, setAllDocuments] = useState(Object.keys(DOCUMENT_NAMES));
    const [documentMeta, setDocumentMeta] = useState({});
    const [completedDocuments, setCompletedDocuments] = useState([]);
    const [activeDocument, setActiveDocument] = useState(null);
    const [documentIds, setDocumentIds] = useState({});
    const [isReconsent, setIsReconsent] = useState(false);
    const [questionsFormUrl, setQuestionsFormUrl] = useState(null);
    const isAdvancingRef = useRef(false);
    const fetchGenerationRef = useRef(0);
    const postSignPollRef = useRef(0);

    const fetchConsentData = async ({ clearActiveDocument = false } = {}) => {
        const generation = ++fetchGenerationRef.current;
        try {
            const token = await getAccessTokenSilently();
            const [urlsResponse, statusResponse] = await Promise.all([
                getEmbeddedSigningUrls(token),
                getConsentStatus(token)
            ]);

            if (urlsResponse.error) throw urlsResponse.error;
            if (statusResponse.error) throw statusResponse.error;
            // A newer refresh started while this one was in flight — ignore.
            if (generation !== fetchGenerationRef.current) {
                return statusResponse.data;
            }

            setSigningUrls(urlsResponse.data);
            setConsentStatus(statusResponse.data);

            const reconsent = statusResponse.data?.is_reconsent || false;
            setIsReconsent(reconsent);
            setQuestionsFormUrl(statusResponse.data?.questions_form_url || null);

            const documentMeta = statusResponse.data?.document_meta || [];
            const metaByType = {};
            documentMeta.forEach((doc) => {
                metaByType[doc.document_type] = doc;
            });
            setDocumentMeta(metaByType);

            // Flow-required docs: prefer document_meta order; fall back to
            // pending required_documents; then legacy hardcoded keys.
            const flowRequiredDocs = documentMeta.length
                ? documentMeta.map((d) => d.document_type)
                : (statusResponse.data?.consents?.required_documents
                    || Object.keys(DOCUMENT_NAMES));
            const pendingDocs = statusResponse.data?.required_documents || [];
            setAllDocuments(reconsent ? pendingDocs : flowRequiredDocs);
            if (statusResponse.data?.consents) {
                const docIds = {};
                Object.entries(statusResponse.data.consents).forEach(([key, value]) => {
                    if (value && typeof value === 'object' && value.document_id) {
                        docIds[key] = value.document_id;
                    }
                });
                setDocumentIds(docIds);
            }

            const completed = memberSignedDocTypes(statusResponse.data);
            setCompletedDocuments(completed);

            const nextActive = Object.entries(urlsResponse.data || {})
                .find(([key, value]) =>
                    value !== null && !completed.includes(key)
                );

            if (clearActiveDocument || !nextActive) {
                setActiveDocument(null);
            } else {
                setActiveDocument({
                    key: nextActive[0],
                    signUrl: nextActive[1].sign_url
                });
            }

            if (
                !nextActive
                && !isManualNavigation
                && !isAdvancingRef.current
                && statusResponse.data?.member_consents_completed
            ) {
                isAdvancingRef.current = true;
                console.log('Auto-continuing to next step - both consents completed');
                setTimeout(async () => {
                    try {
                        await completeStep('CONSENT');
                    } catch (err) {
                        console.error('Auto-continue failed:', err);
                        isAdvancingRef.current = false;
                    }
                }, 1500);
            }

            return statusResponse.data;
        } catch (err) {
            if (generation === fetchGenerationRef.current) {
                setError(err);
            }
            return null;
        }
    };

    const pollUntilDocumentSigned = async (documentType) => {
        const pollId = ++postSignPollRef.current;
        setUpdatingAfterSign(true);
        setError(null);

        try {
            for (let attempt = 0; attempt < POST_SIGN_POLL_ATTEMPTS; attempt += 1) {
                if (pollId !== postSignPollRef.current) {
                    return null;
                }
                const status = await fetchConsentData({ clearActiveDocument: true });
                if (pollId !== postSignPollRef.current) {
                    return status;
                }
                if (status?.consents?.[documentType]?.member_signed) {
                    return status;
                }
                if (attempt < POST_SIGN_POLL_ATTEMPTS - 1) {
                    await sleep(POST_SIGN_POLL_DELAY_MS);
                }
            }
            setError('Signature received but status has not updated yet. Please refresh.');
            return null;
        } finally {
            if (pollId === postSignPollRef.current) {
                setUpdatingAfterSign(false);
                setActiveDocument(null);
            }
        }
    };

    useEffect(() => {
        const initializePage = async () => {
            setLoading(true);
            await fetchConsentData();
            setLoading(false);
        };

        initializePage();
    }, [getAccessTokenSilently]);



    const handleSignSpecific = async (documentType) => {
        setActiveDocument({ key: documentType, signUrl: null });
        setUpdatingAfterSign(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();
            const urlsResponse = await getEmbeddedSigningUrls(token);

            if (urlsResponse.error) throw urlsResponse.error;

            setSigningUrls(urlsResponse.data);

            const freshUrl = urlsResponse.data?.[documentType]?.sign_url;
            if (!freshUrl) {
                setError('Unable to load signing form. Please try again.');
                setActiveDocument(null);
                setUpdatingAfterSign(false);
                return;
            }

            setActiveDocument({ key: documentType, signUrl: freshUrl });
            setUpdatingAfterSign(false);

            const client = new HelloSign();
            setIsSigningVisible(true);
            let signedDocument = false;

            // Dropbox Sign can lag after the embedded `sign` event. Poll the
            // status endpoint (same pattern as clinic ConsentStepPanel) so the
            // UI goes green without depending on a single close-time refresh.
            client.on('sign', async (data) => {
                console.log(`Document ${documentType} signed:`, data.signatureId);
                signedDocument = true;
                await pollUntilDocumentSigned(documentType);
            });

            client.on('close', async () => {
                setIsSigningVisible(false);
                if (signedDocument) {
                    // Sign handler already owns the post-sign refresh.
                    return;
                }
                setUpdatingAfterSign(true);
                try {
                    await fetchConsentData({ clearActiveDocument: true });
                } catch (err) {
                    setError(err);
                } finally {
                    setUpdatingAfterSign(false);
                    setActiveDocument(null);
                }
            });

            client.open(freshUrl, {
                clientId: DROPBOX_SIGN_CLIENT_ID,
                skipDomainVerification: true
            });
        } catch (err) {
            setError('Unable to load signing form. Please try again.');
            setActiveDocument(null);
            setUpdatingAfterSign(false);
        }
    };

    const handleComplete = async () => {
        if (isAdvancingRef.current) return;
        isAdvancingRef.current = true;
        try {
            setLoading(true);
            const status = await fetchConsentData();

            if (status?.member_consents_completed) {
                await completeStep('CONSENT');
            } else {
                setError('Please sign all consent forms before proceeding.');
                isAdvancingRef.current = false;
            }
        } catch (err) {
            setError(err);
            isAdvancingRef.current = false;
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (docKey) => {
        try {
            const token = await getAccessTokenSilently();
            const documentId = documentIds[docKey];
            
            if (!documentId) {
                throw new Error('Document ID not found');
            }

            const response = await downloadConsentDocument(token, documentId);
            
            if (response.error) {
                throw response.error;
            }

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${DOCUMENT_NAMES[docKey]}.pdf`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
            setError(err);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="content-layout">
                    <OnboardingProgressBar />
                    <PageLoader />
                </div>
            </PageLayout>
        );
    }

    const getDocumentStatus = (docKey) => {
        if (completedDocuments.includes(docKey)) {
            return (
                <div className="document-actions">
                    <Chip label="Completed" color="success" size="small" />
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => handleDownload(docKey)}
                    >
                        Download
                    </Button>
                </div>
            );
        }
        if (activeDocument?.key === docKey) {
            return <Chip label="Now" color="warning" size="small" />;
        }
        return <Chip label="Next" color="warning" size="small" />;
    };

    // Rest of the component remains the same, but use these conditions:
    const isCurrentDocSigned = activeDocument === null;
    const allDocsSigned = allDocuments.length > 0 && allDocuments.every(doc => completedDocuments.includes(doc));

    return (
        <PageLayout>
            <div className="content-layout">
                <OnboardingProgressBar />
                <FormShell>
                    <h1 className="content__title">
                        {isReconsent ? 'Updated Informed Consent' : 'Informed Consent'}
                    </h1>
                    {isManualNavigation && allDocsSigned ? (
                        <>
                            <p className="content__body"><strong>✓ Your consents are complete!</strong> You can download your signed documents below or continue to the next step.</p>
                        </>
                    ) : isReconsent ? (
                        <>
                            <p className="content__body">Our Informed Consent Form (ICF) has been updated. Please review and sign the updated consent form below to continue your participation in the study.</p>
                        </>
                    ) : (
                        <>
                            <p className="content__body">In order to enroll in this study, we'll have to get your informed consent.</p>
                            <p className="content__body">Please carefully review and sign the consent forms below to continue.</p>
                        </>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', margin: '2rem 0' }}>
                        {allDocuments.map((docType) => {
                            const meta = documentMeta[docType] || {};
                            const title = meta.label || DOCUMENT_NAMES[docType] || docType;
                            let description = meta.description;
                            if (!description) {
                                if (docType === 'cosigned') {
                                    description = isReconsent
                                        ? "The study's Informed Consent Form has been updated. Please review the changes and re-sign to continue your participation."
                                        : "This form provides your informed consent to participate in the research study. It outlines the study procedures, potential risks and benefits, and your rights as a participant.";
                                } else if (docType === 'pluto') {
                                    description = "This form provides consent for Pluto Health to access and share your health records with Onyx for study purposes. This enables us to verify your diagnosis and medical history.";
                                } else {
                                    description = "Please review and sign this consent form.";
                                }
                            }
                            return (
                                <ConsentFormComponent
                                    key={docType}
                                    documentType={docType}
                                    title={title}
                                    description={description}
                                    onSign={handleSignSpecific}
                                    isSigned={completedDocuments.includes(docType)}
                                    isLoading={updatingAfterSign && activeDocument?.key === docType}
                                    onDownload={handleDownload}
                                />
                            );
                        })}
                    </div>
                    {updatingAfterSign ? (
                        <OnyxBanner severity="info">
                            Processing your signature, please wait...
                        </OnyxBanner>
                    ) : null}

                    {allDocsSigned ? (
                        <OnyxBanner severity="success">
                            <strong>✓ All consent forms completed!</strong>
                            {' '}
                            You can now proceed to the next step.
                        </OnyxBanner>
                    ) : null}

                    {questionsFormUrl ? (
                        <p className="content__body">
                            Questions? Fill out{' '}
                            <a
                                href={questionsFormUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                this form
                            </a>
                            {' '}and a member of Attune Health&apos;s staff will reach out to
                            walk you through the study in more detail.
                        </p>
                    ) : null}
                </FormShell>
            </div>
        </PageLayout>
    );
};